/**
 * LibraryPicker — inline picker for selecting a mockup background from the shared library.
 * Opens a compact grid below the "Pick from Library" button.
 */
import { useState, useEffect, useCallback } from "react";
import { Frame, Search, X } from "lucide-react";
import adminApi from "@/lib/adminApi";

export default function LibraryPicker({ onSelect, productType, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (productType) params.set("product_type", productType);
      const res = await adminApi.get(`/api/admin/templates/mockup-library?${params}`);
      setItems(res.data.items || []);
    } catch (err) { console.error("[LibraryPicker] fetch failed:", err); }
    setLoading(false);
  }, [productType]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = items.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return item.name.toLowerCase().includes(q) || (item.tags || []).some((t) => t.includes(q));
  });

  return (
    <div className="border border-[#CBA052]/20 bg-[#0a0a0a] rounded mt-2 p-3" data-testid="library-picker">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Frame size={11} className="text-[#CBA052]" />
        <span className="text-[10px] font-bold text-[#CBA052] uppercase tracking-wider">Library</span>
        <span className="text-[9px] text-white/25">{items.length} backgrounds</span>
        <button onClick={onClose} className="ml-auto text-white/25 hover:text-white"><X size={11} /></button>
      </div>

      {/* Search */}
      <div className="relative mb-2">
        <Search size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/15" />
        <input
          className="w-full bg-white/[0.03] border border-white/[0.06] text-white text-[10px] rounded pl-6 pr-2 py-1.5 outline-none focus:border-[#CBA052]/30"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="library-picker-search"
        />
      </div>

      {/* Grid */}
      {loading ? (
        <p className="text-[10px] text-white/20 text-center py-4">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-[10px] text-white/20 text-center py-4">
          {items.length === 0 ? "No backgrounds in library. Add some in the Mockup Library section above." : "No match"}
        </p>
      ) : (
        <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto">
          {filtered.map((item) => (
            <button
              key={item.id}
              onClick={() => onSelect(item)}
              className="group relative rounded overflow-hidden border border-white/[0.06] hover:border-[#CBA052]/40 transition-colors"
              title={item.name}
              data-testid={`library-pick-${item.id}`}
            >
              <div className="aspect-[3/2] bg-black/50">
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
              <p className="text-[8px] text-white/50 p-1 truncate">{item.name}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
